import { isMobileDevice } from '../utils/DeviceDetection.js';
import * as THREE from 'three';

export class UI {
  constructor() {
    this.loadingScreen = document.getElementById('loading-screen');
    this.loadingBar = document.getElementById('loading-bar');
    this.loadingText = document.getElementById('loading-text');
    this.uiOverlay = document.getElementById('ui-overlay');
    this.instructionsPanel = document.getElementById('instructions-panel');
    this.paintingInfo = document.getElementById('painting-info');
    this.paintingDescription = document.getElementById('painting-description');

    // New elements
    this.optionsPanel = document.getElementById('options-panel');
    this.startButton = document.getElementById('start-button');
    this.minimapToggle = document.getElementById('minimap-toggle');
    this.minimapContainer = document.getElementById('minimap-container');

    // State
    this.minimapEnabled = false;
    this.onStartCallback = null;
    this.isMobile = isMobileDevice();

    this.setupEventListeners();
    this.updateInstructionsForDevice();
  }

  updateInstructionsForDevice() {
    // Update loading screen instructions
    const loadingInstructions = document.querySelector('.instructions');
    if (loadingInstructions) {
      if (this.isMobile) {
        loadingInstructions.innerHTML = `
          <p><strong>Mobile Controls:</strong></p>
          <p>Left Joystick - Move</p>
          <p>Right Joystick - Look around</p>
        `;
      } else {
        loadingInstructions.innerHTML = `
          <p><strong>Controls:</strong></p>
          <p>WASD - Move</p>
          <p>Mouse - Look around</p>
          <p>M - Toggle minimap</p>
        `;
      }
    }

    // Update in-game instructions panel
    if (this.instructionsPanel) {
      if (this.isMobile) {
        this.instructionsPanel.innerHTML = `
          <h2>Controls</h2>
          <p><strong>Left Joystick</strong> - Move around</p>
          <p><strong>Right Joystick</strong> - Look around</p>
        `;
      } else {
        this.instructionsPanel.innerHTML = `
          <h2>Controls</h2>
          <p><strong>WASD</strong> - Move around</p>
          <p><strong>Mouse</strong> - Look around</p>
          <p><strong>M</strong> - Toggle minimap</p>
          <p><strong>I</strong> - Toggle instructions</p>
          <p><strong>ESC</strong> - Release mouse</p>
        `;
      }
    }
  }

  setupEventListeners() {
    // Toggle instructions with 'I' key (desktop only)
    if (!this.isMobile) {
      document.addEventListener('keydown', (e) => {
        if (e.code === 'KeyI') {
          this.toggleInstructions();
        }
        // Toggle minimap with 'M' key
        if (e.code === 'KeyM') {
          this.toggleMinimap();
        }
      });
    }

    // Start button click
    if (this.startButton) {
      this.startButton.addEventListener('click', () => {
        if (this.onStartCallback) {
          this.onStartCallback();
        }
      });
    }

    // Minimap toggle change
    if (this.minimapToggle) {
      this.minimapToggle.addEventListener('change', (e) => {
        this.minimapEnabled = e.target.checked;
      });
    }
  }

  /**
   * Set callback for when start button is clicked
   */
  onStart(callback) {
    this.onStartCallback = callback;
  }

  /**
   * Show the options panel and start button (called when loading is complete)
   */
  showStartOptions() {
    if (this.optionsPanel) {
      this.optionsPanel.classList.remove('hidden');
    }
    if (this.startButton) {
      this.startButton.classList.remove('hidden');
    }
    this.updateLoadingText('Ready to enter');
  }

  /**
   * Get current minimap enabled state
   */
  isMinimapEnabled() {
    return this.minimapEnabled;
  }

  /**
   * Toggle minimap visibility
   */
  toggleMinimap() {
    this.minimapEnabled = !this.minimapEnabled;

    // Update checkbox state
    if (this.minimapToggle) {
      this.minimapToggle.checked = this.minimapEnabled;
    }

    // Update minimap container visibility
    if (this.minimapContainer) {
      if (this.minimapEnabled) {
        this.minimapContainer.classList.remove('hidden');
      } else {
        this.minimapContainer.classList.add('hidden');
      }
    }
  }

  /**
   * Show minimap
   */
  showMinimap() {
    if (this.minimapContainer && this.minimapEnabled) {
      this.minimapContainer.classList.remove('hidden');
    }
  }

  /**
   * Hide minimap
   */
  hideMinimap() {
    if (this.minimapContainer) {
      this.minimapContainer.classList.add('hidden');
    }
  }

  updateLoadingProgress(progress) {
    const percentage = Math.round(progress * 100);
    this.loadingBar.style.width = `${percentage}%`;
    this.loadingText.textContent = `Loading assets... ${percentage}%`;
  }

  updateLoadingText(text) {
    this.loadingText.textContent = text;
  }

  hideLoadingScreen() {
    this.loadingScreen.classList.add('fade-out');
    setTimeout(() => {
      this.loadingScreen.style.display = 'none';
      this.uiOverlay.classList.remove('hidden');

      // Show minimap if enabled
      if (this.minimapEnabled) {
        this.showMinimap();
      }
    }, 500);
  }

  showLoadingScreen() {
    this.loadingScreen.style.display = 'flex';
    this.loadingScreen.classList.remove('fade-out');
    this.uiOverlay.classList.add('hidden');
  }

  toggleInstructions() {
    this.instructionsPanel.classList.toggle('hidden');
  }

  showInstructions() {
    this.instructionsPanel.classList.remove('hidden');
  }

  hideInstructions() {
    this.instructionsPanel.classList.add('hidden');
  }

  showPaintingInfo(title, description) {
    this.paintingDescription.textContent = description;
    this.paintingInfo.classList.remove('hidden');
  }

  hidePaintingInfo() {
    this.paintingInfo.classList.add('hidden');
  }

  /**
   * Project 3D world position to 2D screen coordinates
   */
  projectToScreen(position, camera, renderer) {
    const vector = new THREE.Vector3(position.x, position.y, position.z);
    vector.project(camera);

    const widthHalf = renderer.domElement.width / 2;
    const heightHalf = renderer.domElement.height / 2;

    return {
      x: (vector.x * widthHalf) + widthHalf,
      y: -(vector.y * heightHalf) + heightHalf
    };
  }

  updatePaintingInfo(cameraPosition, paintings, camera, renderer) {
    // Find the closest painting
    let closestPainting = null;
    let closestDistance = Infinity;

    paintings.forEach(painting => {
      // Calculate distance using Euclidean distance formula
      const dx = cameraPosition.x - painting.data.position.x;
      const dy = cameraPosition.y - painting.data.position.y;
      const dz = cameraPosition.z - painting.data.position.z;
      const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

      if (distance < closestDistance && distance < 4) { // Only show if within 4 units
        closestDistance = distance;
        closestPainting = painting;
      }
    });

    if (closestPainting) {
      const info = closestPainting.getPaintingInfo();
      this.showPaintingInfo(info.title, info.description);

      // Position the info panel next to the painting
      this.positionPaintingInfo(closestPainting, camera, renderer);
    } else {
      this.hidePaintingInfo();
    }
  }

  /**
   * Position the painting info next to the painting on the right side
   */
  positionPaintingInfo(painting, camera, renderer) {
    // Get painting position in 3D space
    const paintingPos = painting.data.position;

    // Calculate the right edge of the painting based on its rotation and scale
    const paintingWidth = painting.data.scale.width || 2;
    const offset = paintingWidth / 2 + 0.8; // Half width + margin

    // Create a position to the right of the painting
    // We need to account for the painting's Y rotation (yaw)
    const rotationY = painting.data.rotation.y;

    // Calculate right direction based on painting's rotation
    // Painting faces along -Z axis by default, right is +X
    // After rotating by rotationY, right direction rotates as well
    const rightOffset = new THREE.Vector3(
      Math.cos(rotationY) * offset,
      0,
      Math.sin(rotationY) * offset
    );

    const infoPosition = {
      x: paintingPos.x + rightOffset.x,
      y: paintingPos.y,
      z: paintingPos.z + rightOffset.z
    };

    // Project to screen coordinates
    const screenPos = this.projectToScreen(infoPosition, camera, renderer);

    // Get info panel dimensions
    const infoPanelWidth = this.paintingInfo.offsetWidth || 300;
    const infoPanelHeight = this.paintingInfo.offsetHeight || 100;

    // Keep within screen bounds
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    let finalX = screenPos.x;
    let finalY = screenPos.y - infoPanelHeight / 2; // Center vertically on painting

    // Clamp to screen bounds with padding
    const padding = 20;
    finalX = Math.max(padding, Math.min(finalX, screenWidth - infoPanelWidth - padding));
    finalY = Math.max(padding, Math.min(finalY, screenHeight - infoPanelHeight - padding));

    // Apply position
    this.paintingInfo.style.left = `${finalX}px`;
    this.paintingInfo.style.top = `${finalY}px`;
    this.paintingInfo.style.transform = 'none'; // Remove centering transform
  }
}
